import React, { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import { IComment, ICommentContent } from "interfaces/comment";
import { CommentFeed } from "components/Comments/CommentFeed";
import { styled } from "@mui/material/styles";

function getShowHideButtonText(
  numComments: number,
  commentsAreExpanded: boolean
): string {
  switch (numComments) {
    case 1:
      return commentsAreExpanded ? "HIDE 1 COMMENT" : "SHOW 1 COMMENT";
    default:
      return commentsAreExpanded
        ? `HIDE ${numComments} COMMENTS`
        : `SHOW ${numComments} COMMENTS`;
  }
}

export interface CommentFeedAccordionProps {
  loading: boolean;
  comments: IComment[];
  addComment: (content: ICommentContent) => Promise<IComment>;
  updateComment: (
    commentId: number,
    content: ICommentContent
  ) => Promise<IComment>;
  deleteComment: (commentId: number) => Promise<void>;
}

export const CommentFeedAccordion = ({
  loading,
  comments,
  addComment,
  updateComment,
  deleteComment
}: CommentFeedAccordionProps): JSX.Element => {
  // This is a list of rendered comments, collapsible via a toggle button

  const [commentsAreExpanded, setCommentsAreExpanded] = useState(true);

  const commentButtonText = useMemo(
    () => getShowHideButtonText(comments.length, commentsAreExpanded),
    [comments, commentsAreExpanded]
  );

  return (
    <>
      {comments.length > 0 && (
        <CollapseToggleButton
          onClick={() => setCommentsAreExpanded(prevValue => !prevValue)}
          color="primary"
          variant="outlined"
          data-qa="CP-Button-Toggle-Comments"
        >
          {commentButtonText}
        </CollapseToggleButton>
      )}
      <Collapse
        in={commentsAreExpanded}
        timeout="auto"
        unmountOnExit
        data-qa="CP-Comments-Section"
      >
        <CommentFeed
          loading={loading}
          comments={comments}
          addComment={addComment}
          updateComment={updateComment}
          deleteComment={deleteComment}
        />
      </Collapse>
    </>
  );
};

const CollapseToggleButton = styled(Button)`
  margin: 0px 6px 12px 6px;
`;
